/**
 * Tokens de cor dos gráficos da carteira.
 *
 * A base é a paleta já validada do painel de squads (mesmas superfícies reais do
 * app: #FFFFFF no claro, #2D2D2D no escuro) — aqui só se acrescenta o que a
 * carteira precisa a mais: uma rampa ordinal de tempo e a paleta de status.
 *
 * Duas convenções semânticas valem na tela inteira, e é por isso que só há duas
 * cores categóricas: **azul = dinheiro que já entrou** (realizado/faturado),
 * **amarelo = dinheiro que ainda não entrou** (previsto/pendente). A cor segue a
 * natureza do valor, nunca a posição no gráfico.
 *
 * Ressalvas que a validação impõe e a tela cumpre:
 *  - no claro o amarelo fica em 2.17:1 contra o branco → todo gráfico que o usa
 *    traz rótulo direto e a tabela equivalente logo abaixo, nunca só a cor;
 *  - a paleta de status não passa no piso de visão normal entre "atrasado" e
 *    "parado" (ΔE 13.6) → status aparece sempre como chip com ícone + texto, e
 *    nunca como única codificação num gráfico.
 */

import { obterVizTheme, type Modo, type VizTheme } from '@/pages/squads/squad-viz-theme';

export type { Modo, VizTheme };
export { obterVizTheme };

/** Azul = já entrou. Amarelo = ainda não entrou. */
export interface CoresValor {
  realizado: string;
  previsto: string;
}

const VALOR_CLARO: CoresValor = { realizado: '#1f5fa9', previsto: '#eda100' };
const VALOR_ESCURO: CoresValor = { realizado: '#3987e5', previsto: '#c98500' };

export const obterCoresValor = (modo: Modo): CoresValor =>
  modo === 'dark' ? VALOR_ESCURO : VALOR_CLARO;

/**
 * Rampa ordinal de "há quanto tempo não fatura": um só tom de azul, com passos
 * de luminosidade monotônicos. No claro vai do claro ao escuro; no escuro o
 * sentido se inverte, senão o passo mais severo somem no fundo.
 * Validada com `--ordinal` contra as duas superfícies do app.
 */
const AGING_CLARO = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#104281'];
const AGING_ESCURO = ['#256abf', '#3987e5', '#6da7ec', '#9ec5f4', '#cde2fb'];

export const obterRampaAging = (modo: Modo): string[] =>
  modo === 'dark' ? AGING_ESCURO : AGING_CLARO;

/**
 * Paleta de status — fixa, não temática, e reservada: nenhuma dessas cores
 * serve de série num gráfico. Só aparece em chip, sempre acompanhada de texto.
 */
export const STATUS_CORES = {
  bom: '#0ca30c',
  atencao: '#fab219',
  serio: '#ec835a',
  critico: '#d03b3b',
} as const;
